import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  Grid,
  Table,
} from "semantic-ui-react";
import { isEmpty } from "lodash";
import { useForm } from "react-hook-form";

import {
  AccordionTitleView,
  CommonTable,
  CustomButton,
  DropDown,
} from "../../../../../components";
import { InputNumber } from "../../../../../components/InputText/InputNumber";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";
import PlusIcon from "../../../../../assets/commonIcon/PlusIcon";
import { CULTIVATION_TYPE, ENERGY_UNIT_TYPE, EnergySourceData } from "../../../../../config/constants";
import { useGetEnergySource } from "../../../../../api/static-data";

import "../styles/CultivarForm.scss";
import ConfirmModal from "../../../../../components/confirmViewModal/ConfirmModal";
import { validateCultivationNumber } from "../../../../../utils/utils";

const FuelUse = forwardRef((
  {
    currentFormData,
    currentDataType,
    currentOriginalFormData
  }: any,
  ref
) => {
  const [accordionEnabled, setAccordionEnabled] = useState<boolean>(false);
  const [enableUpdateButton, setEnableUpdateButton] = useState<any>(false);
  const [energySource, setEnergySource] = useState<any>("")
  const [energyUnitType, setEnergyUnitType] = useState<any>("");
  const [fuelUseData, setFuelUseData] = useState<any>([])
  const [onFuelUseEdit, setOnFuelUseEdit] = useState(false)
  const [editIndex, setEditIndex] = useState<any>(null)
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false)
  const [deleteValue, setDeleteValue] = useState<any>({
    item: "",
    type: "",
  });
  const originalFuelUse =
    currentOriginalFormData?.cultivationField?.fuelUse || [];

  const fuelUseDetails = useCallback(() => {
    const currentFualUseData = currentFormData?.cultivationField?.fuelUse?.map(
      (item: any) => {
        return {
          ...item,
          id: parseInt(item.id),
        };
      }
    );
    setFuelUseData(currentFualUseData)
  }, [currentFormData])

  useEffect(() => {
    fuelUseDetails()
  }, [currentFormData])

  const {
    data: energySourceData,
  } = useGetEnergySource();

  const values = useMemo(() => {
    return {
      energyUsed: "",
    }
  }, [currentFormData])

  const {
    formState: { errors },
    control,
    setValue,
    handleSubmit
  } = useForm({ defaultValues: values, values: values });

  const resetFuelUsed = () => {
    setEnergySource("");
    setEnergyUnitType("");
    setValue("energyUsed", "");
  };

  useImperativeHandle(ref, () => ({
    getFuelUse: () => {
      return fuelUseData
    }
  }));

  const onEditFuelUse = (item: any, index: any) => {
    setOnFuelUseEdit(true);
    setEditIndex(index);
    setValue("energyUsed", item?.energyUsed ?? "");
    setEnergySource(item.energySource.toString());
    setEnergyUnitType(item.energyUnit);
  }

  const AddFuelUse = handleSubmit((values) => {
    const fuelUsedEntry = {
      energySource: Number(energySource),
      energyUsed: Number(values.energyUsed),
      energyUnit: Number(energyUnitType),
    };

    if (onFuelUseEdit) {
      const updatedData = [...fuelUseData];
      updatedData[editIndex] = { ...updatedData[editIndex], ...fuelUsedEntry };
      setFuelUseData(updatedData);
      setOnFuelUseEdit(false);
    } else {
      setFuelUseData((prev: any) => [...prev, fuelUsedEntry]);
    }
    resetFuelUsed();
  });

  const deleteFuelUse = useCallback(() => {
    setFuelUseData((prevList: any[]) =>
      prevList.filter((_, index: number) => index !== deleteValue.index)
    );
    setVisibleDeleteModal(false);
    setDeleteValue({});
  }, [deleteValue])

  const handleClick = () => {
    setAccordionEnabled(!accordionEnabled);
  };

  return currentDataType === CULTIVATION_TYPE.Supplier ? (
    <div>
      <AccordionTitleView
        accordionEnabled={false}
        title={"Fuel Use Details"}
        plusIcon={false}
      />
      <div>
        <Grid>
          <Grid.Row divided>
            <Grid.Column computer={8} tablet={16} mobile={16}>
              {originalFuelUse.length > 0 && (
                <Grid.Column
                  computer={16}
                  tablet={16}
                  mobile={16}
                  className="pesticideTable"
                >
                  <CommonTable
                    tableHeaderData={EnergySourceData.filter(
                      (e: any) => e.name !== ""
                    )}
                    className="pesticideTable"
                    isSimulateView
                  >
                    {originalFuelUse?.map((item: any, index: any) => {
                      return (
                        <Table.Row key={`pesticide_${item?.id}`}>
                          <Table.Cell>
                            <p>
                              {energySourceData?.find(
                                (e: any) => e?.value == item?.energySource
                              )?.text ?? ""}
                            </p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>{item?.energyUsed ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>
                              {ENERGY_UNIT_TYPE?.find(
                                (e: any) => e?.key == item?.energyUnit
                              )?.text ?? ""}
                            </p>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </CommonTable>
                </Grid.Column>
              )}
            </Grid.Column>
            <Grid.Column computer={8} tablet={16} mobile={16}>
              <Grid>
                <Grid.Column computer={5} tablet={8} mobile={16}>
                  <DropDown
                    labelName={"Energy Source"}
                    placeholder="Select Energy Source"
                    defaultValue={energySource}
                    currentData={energySourceData}
                    handleChangeState={(e: any, { value }: any) => {
                      setEnergySource(value);
                    }}
                    search
                    key={"energy_source"}
                  />
                </Grid.Column>
                <Grid.Column computer={5} tablet={6} mobile={16}>
                  <InputNumber
                    transform={{
                      input: (value: any) =>
                        isNaN(value) || value === 0 ? "" : value.toString(),
                      output: (e: any) => {
                        return e;
                      },
                    }}
                    control={control}
                    labelName={"Energy Used"}
                    placeholder="Energy Used"
                    name="energyUsed"
                    errors={errors?.energyUsed}
                    required
                    errorMessage={"Energy used required"}
                    otherValidation={{
                      validate: {
                        valueCheck: (v: any) => validateCultivationNumber(v)
                      }
                    }}
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={8} mobile={16}>
                  <DropDown
                    labelName={"Energy Unit type"}
                    placeholder="Select Energy Unit type"
                    defaultValue={energyUnitType}
                    currentData={ENERGY_UNIT_TYPE}
                    handleChangeState={(e: any, { value }: any) => {
                      setEnergyUnitType(value);
                    }}
                    search
                    key={"energy_unit"}
                  />
                </Grid.Column>
                <Grid.Column computer={2} tablet={2} mobile={16}>
                  {enableUpdateButton ?
                    <CustomButton
                      theme="green"
                      title={enableUpdateButton ? "Update" : ""}
                      icon={!enableUpdateButton ? "plus" : null}
                      disabled={
                        isEmpty(energySource.toString()) ||
                        isEmpty(energyUnitType.toString())
                      }
                      customColumnStyle={"updateIrrgation"}
                      onClick={() => {
                        AddFuelUse();
                        setEnableUpdateButton(false);
                      }}
                    /> :
                    <CustomButton
                      theme="green"
                      customColumnStyle={"addUnitSize"}
                      icon
                      iconChild={<PlusIcon />}
                      customBase="productUnitAddButton"
                      onClick={() => {
                        AddFuelUse();
                        setEnableUpdateButton(false);
                      }}
                    />
                  }
                </Grid.Column>
              </Grid>
              {fuelUseData?.length > 0 && (
                <Grid.Column
                  computer={16}
                  tablet={16}
                  mobile={16}
                  className="pesticideTable"
                >
                  <CommonTable
                    tableHeaderData={EnergySourceData}
                    className="pesticideTable"
                  >
                    {fuelUseData?.map((item: any, index: any) => {
                      return (
                        <Table.Row key={`pesticide_${item?.id}`}>
                          <Table.Cell>
                            <p>
                              {energySourceData?.find(
                                (e: any) => e?.value == item?.energySource
                              )?.text ?? ""}
                            </p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>{item?.energyUsed ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>
                              {ENERGY_UNIT_TYPE?.find(
                                (e: any) => e?.key == item?.energyUnit
                              )?.text ?? ""}
                            </p>
                          </Table.Cell>
                          <Table.Cell>
                            <Grid>
                              <Grid.Column computer={8} tablet={16} mobile={16}>
                                <MdModeEditOutline
                                  cursor={"pointer"}
                                  size={24}
                                  color="var(--tableEditIcon)"
                                  onClick={() => {
                                    onEditFuelUse(item, index);
                                    setEnableUpdateButton(true);
                                  }}
                                />
                              </Grid.Column>
                              <Grid.Column computer={8} tablet={16} mobile={16}>
                                <MdDeleteOutline
                                  cursor={"pointer"}
                                  size={24}
                                  color="var(--tableEditIcon)"
                                  onClick={() => {
                                    setVisibleDeleteModal(true);
                                    setDeleteValue({
                                      type: "FuelUsed",
                                      item: item ?? "",
                                      index: index,
                                    });
                                  }}
                                />
                              </Grid.Column>
                            </Grid>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </CommonTable>
                </Grid.Column>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
      <ConfirmModal
        viewModal={visibleDeleteModal}
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => {
          setVisibleDeleteModal(false);
          setDeleteValue({});
        }}
        approve={deleteFuelUse}
        title={`Delete ${deleteValue.type}`}
        subTitle={`Are you sure you want to delete Fuel use data?`}
      />
    </div>
  ) : null;
})

export default FuelUse;
